/**
 * ModeStrip - the actor's one strip above the tabs (plan modes-rework, stage 5; the stance strip and the kill-team
 * strip before it):
 *
 *   MODE      - for a brother of a kill-team with an Oath: Solo | Squad, the Cohesion Challenge while he may enter,
 *               his kill-team's Cohesion, the linked chevrons
 *   PLATES    - the current mode's: in Solo his Solo modes (a one-at-a-time group under its caption, then the free
 *               ones) and his triggered Always on items (lit by their condition, never pressed); in Squad the Oath's
 *               abilities - start one, join one running - each dimmed with its reason
 *   IN FORCE  - every effect now up, named by what brought it
 *
 * Every Squad Mode press is the drum's own (`pressSquad`, useKillTeamDrum): the same plates, reasons and charge -
 * Squad Mode a Full Action, an ability its own. Draws nothing for a character with none of it, most of the table.
 */
import { useEffect } from 'react';
import { useKillTeamsContext } from '../contexts/KillTeamsContext';
import { effectSentence } from '../engine/character/effectItems';
import {
  soloGroups,
  triggerSentence,
  upEffects,
  upNow,
  type EffectItem,
  type SoloMode,
} from '../engine/character/soloModes';
import { teamOf, type Livery } from '../engine/roster/team';
import type { Combatant } from '../engine/roster/types';
import type { ActionDef } from '../engine/actions/types';
import type { KillTeamDrum } from '../hooks/useKillTeamDrum';
import { setSquadPreview } from '../hooks/useSquadPreview';
import LinkedChevrons from './killTeam/LinkedChevrons';
import styles from '../styles/StanceStrip.module.css';

interface Props {
  actor: Combatant | null;
  livery?: Livery | null;
  /** The table has this seat's controls closed. */
  locked?: boolean;
  /** Switch one of his Solo modes (useSoloToggle). */
  onToggleSolo: (id: string) => void;
  /** His Squad Mode run (useKillTeamDrum); null for anyone who is no brother of a kill-team with an Oath. */
  squad: KillTeamDrum | null;
  /** The run's press, charged as the drum charges it. */
  pressSquad: (id: string) => Promise<boolean>;
}

/**
 * Aiming at Squad or Challenge (hovered or focused) previews his support ring on the map (2026-10-24). A press lets go
 * of it: the button it lands on may go disabled, and a disabled button is never left.
 */
const previewing = (on: boolean) => () => setSquadPreview('strip', on);
const PREVIEW = {
  onPointerEnter: previewing(true),
  onPointerLeave: previewing(false),
  onFocus: previewing(true),
  onBlur: previewing(false),
};

type SquadProps = { actor: Combatant; squad: KillTeamDrum; press: (id: string) => void; locked: boolean };

/** A plate of the Squad Mode run, by its id (`kt:enter`, ...): pressed through the run, dimmed with its reason. */
function RunButton(p: SquadProps & { id: string; testId: string; label: string; radio?: boolean; on?: boolean }) {
  const why = p.squad.reasons[p.id] ?? null;
  const def = p.squad.defs.find((d) => d.id === p.id);
  return (
    <button
      type="button"
      role={p.radio ? 'radio' : undefined}
      aria-checked={p.radio ? !!p.on : undefined}
      className={styles.plate}
      data-testid={p.testId}
      disabled={p.locked || !!why}
      title={why ?? def?.blurb}
      {...(p.id === 'kt:leave' ? {} : PREVIEW)}
      onClick={() => {
        setSquadPreview('strip', false);
        p.press(p.id);
      }}
    >
      {p.label}
    </button>
  );
}

/** Solo | Squad, the Challenge while he may enter, and his kill-team's Cohesion. */
function Mode(p: SquadProps & { livery?: Livery | null }) {
  const ks = p.actor.state.killTeam;
  const k = useKillTeamsContext();
  const tag = teamOf(p.actor)?.tag ?? '';
  const kt = k?.teams.killTeams[tag];
  const pool = k?.cohesion?.pools[tag]?.pool;
  // A strip that goes (another actor, a panel closed) never leaves a preview behind.
  useEffect(() => () => setSquadPreview('strip', false), []);
  return (
    <div className={styles.group}>
      <div className={styles.group} role="radiogroup" aria-label="Mode">
        <span className={styles.caption}>Mode</span>
        <RunButton {...p} id="kt:leave" testId="kill-team-mode-solo" label="Solo" radio on={!ks?.on} />
        <RunButton {...p} id="kt:enter" testId="kill-team-mode-squad" label="Squad" radio on={!!ks?.on} />
        {!ks?.on && !p.squad.reasons['kt:challenge'] && (
          <RunButton {...p} id="kt:challenge" testId="kill-team-challenge" label="Challenge" />
        )}
        {ks?.pendingEntry && <span className={styles.caption}>Enters at his next turn</span>}
      </div>
      <LinkedChevrons livery={p.livery} />
      <span
        className={styles.caption}
      >{`${teamOf(p.actor)?.label ?? tag} · ${pool ?? '-'}/${kt?.start.start ?? '-'}`}</span>
    </div>
  );
}

/** The Oath's abilities, in Squad Mode: start one (its action), join one running (free). */
function Abilities(p: SquadProps) {
  const engaged = new Set(p.squad.engaged);
  const abilities = p.squad.defs.filter((d) => d.killTeam?.verb === 'start' || d.killTeam?.verb === 'join');
  const oath = useKillTeamsContext()?.teams.killTeams[teamOf(p.actor)?.tag ?? '']?.oath?.name ?? 'Oath';
  if (!abilities.length) return null;
  return (
    <div className={styles.group} role="group" aria-label={`Squad Mode, ${oath}`}>
      <span className={styles.caption}>{`Squad Mode · ${oath}`}</span>
      {abilities.map((d: ActionDef) => (
        <button
          key={d.id}
          type="button"
          className={styles.plate}
          aria-pressed={engaged.has(d.id)}
          data-testid={`kill-team-ability-${d.killTeam!.abilityId}`}
          data-kind={d.killTeam!.verb}
          disabled={p.locked || !!p.squad.reasons[d.id]}
          title={p.squad.reasons[d.id] ?? d.blurb}
          onClick={() => p.press(d.id)}
        >
          {d.killTeam!.verb === 'start' ? `${d.name} · ${d.killTeam!.cost ?? 0}` : d.name}
        </button>
      ))}
    </div>
  );
}

/** What a plate says on hover: when it is up by itself (if it is), then what it does. */
function plateTitle(bundle: EffectItem | SoloMode) {
  const does = bundle.effects.map(effectSentence).join(' · ') || 'No effects yet';
  const trigger = 'whileOwn' in bundle ? triggerSentence(bundle) : null;
  return trigger ? `${trigger}. ${does}` : does;
}

function Plates(p: {
  list: 'solo' | 'always';
  name: string;
  bundles: (EffectItem | SoloMode)[];
  up: Set<string>;
  locked: boolean;
  on: Props['onToggleSolo'];
}) {
  return (
    <div className={styles.group} role="group" aria-label={p.name}>
      <span className={styles.caption}>{p.name}</span>
      {p.bundles.map((b) => (
        <button
          key={b.id}
          type="button"
          className={styles.plate}
          // Lit by the list's own key (`upNow`): an Always on item and a Solo mode may share an id.
          aria-pressed={p.up.has(`${p.list === 'solo' ? 's' : 'a'}:${b.id}`)}
          data-testid={`${p.list}-${b.id}`}
          // A triggered item follows its condition: shown, lit or not, never pressed.
          disabled={p.locked || ('whileOwn' in b && !!b.whileOwn)}
          title={plateTitle(b)}
          onClick={() => p.on(b.id)}
        >
          {b.name}
        </button>
      ))}
    </div>
  );
}

/** The keys of what is up now, for the plates to light. */
const upKeys = (actor: Combatant) => new Set(upNow(actor.sheet, actor.state).map((u) => u.key));

/** In Solo: his Solo modes, a one-at-a-time group under its caption, then the free ones. */
function SoloPlates({ actor, locked, onToggleSolo }: Pick<Props, 'locked' | 'onToggleSolo'> & { actor: Combatant }) {
  const props = { up: upKeys(actor), locked: !!locked, on: onToggleSolo, list: 'solo' as const };
  return (
    <>
      {soloGroups(actor.sheet.soloModes ?? []).map((g) => (
        <Plates key={g.id} name={g.name} bundles={g.modes} {...props} />
      ))}
    </>
  );
}

/** His triggered Always on items, in either mode (Squad Mode stands only the Solo modes down). */
function TriggerPlates({ actor, onToggleSolo }: Pick<Props, 'onToggleSolo'> & { actor: Combatant }) {
  const triggered = (actor.sheet.alwaysOn ?? []).filter((a) => a.whileOwn);
  if (!triggered.length) return null;
  return <Plates name="Always on" bundles={triggered} up={upKeys(actor)} locked on={onToggleSolo} list="always" />;
}

/** Every effect now up - Always on, Solo modes, squad abilities joined - named by what brought it. */
function InForce({ actor }: { actor: Combatant }) {
  const joined = (actor.state.killTeam?.joined ?? []).flatMap((j) =>
    j.effects.map((effect) => ({ effect, source: j.name })),
  );
  const inForce = [...upEffects(upNow(actor.sheet, actor.state)), ...joined];
  if (!inForce.length) return null;
  return (
    <ul className={styles.effects} data-testid="mode-effects">
      {inForce.map(({ effect, source }, i) => (
        <li key={i}>
          {effectSentence(effect)} <span className={styles.from}>({source})</span>
        </li>
      ))}
    </ul>
  );
}

/** Whether he has anything the strip shows: a kill-team, a Solo mode, a trigger. */
const hasAny = (actor: Combatant, squad: KillTeamDrum | null) =>
  !!squad ||
  // A combatant may come with no sheet at all (a bare stat line): then there is nothing to show.
  (actor.sheet?.soloModes?.length ?? 0) > 0 ||
  (actor.sheet?.alwaysOn ?? []).some((a) => a.whileOwn);

export default function ModeStrip({ actor, livery, locked = false, onToggleSolo, squad, pressSquad }: Props) {
  if (!actor || !hasAny(actor, squad)) return null;
  const inSquad = !!squad && !!actor.state.killTeam?.on;
  const run = squad && { actor, squad, press: (id: string) => void pressSquad(id), locked };
  return (
    <div className={styles.strip} data-testid="mode-strip">
      {run && <Mode {...run} livery={livery} />}
      {run && inSquad ? (
        <Abilities {...run} />
      ) : (
        <SoloPlates actor={actor} locked={locked} onToggleSolo={onToggleSolo} />
      )}
      <TriggerPlates actor={actor} onToggleSolo={onToggleSolo} />
      <InForce actor={actor} />
    </div>
  );
}
